/**
 * Canonical launch-language catalog for live translation — the TypeScript half of AC-1.1.
 *
 * This module is the ONLY place in `ui-frontend` that may spell out the launch
 * output-language codes. Every other surface — pre-join pickers, in-call
 * controls, transcript/caption rendering, report language selectors, API route
 * validation — imports from here. A second hard-coded list anywhere else is a
 * defect, not a convenience: the whole point of AC-1.1 is that a language that
 * is legal on one surface is legal on every surface.
 *
 * Authority: `contracts/live-translation/launch-languages.v1.json`. That file is
 * the cross-runtime source of truth (Python worker, agent-backend, api-ai-engine
 * and this binding all agree with it). This module is a BINDING of that file,
 * not an independent opinion, and `contracts.test.ts` fails if the two drift —
 * including drift against the two JSON Schemas that inline the same enum.
 *
 * Two traps this module exists to prevent:
 *
 *  1. Deepgram `multi` is a source RECOGNITION MODE, not a language. It selects
 *     how source audio is transcribed. It can never be a translation OUTPUT and
 *     is absent from every export here.
 *  2. `ui-frontend/components/conference/CustomPreJoin.tsx` carries a legacy
 *     `SUPPORTED_LANGUAGES` list built for Deepgram STT. It contains `multi`,
 *     `nl` and `ar` (not launch output languages) and omits `id` and `vi`
 *     (which are). It is a source-recognition picker and must never be reused
 *     as the output-language catalog.
 *
 * BI-13 (binder invariant, `conformance-v1.json#/binder_invariants`): language
 * equality and membership are EXACT string comparisons against this catalog.
 * No prefix matching, no substring matching, no locale-fuzzy matching, no
 * natural-language detection. Every predicate below is exact by construction.
 *
 * Changing the catalog is a versioned contract change: publish
 * `launch-languages.v2.json` and bump the schema enums. Never edit v1 in place.
 */

export const LAUNCH_LANGUAGES_CONTRACT_VERSION = 'live-translation.launch-languages.v1';

export type ScriptDirection = 'ltr' | 'rtl';

export interface LaunchLanguage {
  /** BCP-47 primary language subtag, lowercase, no region/script/variant. Join key for every contract field. */
  readonly code: string;
  readonly bcp47: string;
  readonly englishName: string;
  readonly nativeName: string;
  readonly scriptDirection: ScriptDirection;
  /** Exactly one catalog entry is the analysis language. */
  readonly isAnalysisLanguage: boolean;
}

/**
 * THE list. Mirrors `launch-languages.v1.json#/output_languages` in order.
 * Order is contractual: `#/output_language_codes` is the same sequence.
 */
export const LAUNCH_LANGUAGES = [
  { code: 'en', bcp47: 'en', englishName: 'English', nativeName: 'English', scriptDirection: 'ltr', isAnalysisLanguage: true },
  { code: 'es', bcp47: 'es', englishName: 'Spanish', nativeName: 'Español', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'pt', bcp47: 'pt', englishName: 'Portuguese', nativeName: 'Português', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'fr', bcp47: 'fr', englishName: 'French', nativeName: 'Français', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'ja', bcp47: 'ja', englishName: 'Japanese', nativeName: '日本語', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'ru', bcp47: 'ru', englishName: 'Russian', nativeName: 'Русский', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'zh', bcp47: 'zh', englishName: 'Chinese', nativeName: '中文', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'de', bcp47: 'de', englishName: 'German', nativeName: 'Deutsch', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'ko', bcp47: 'ko', englishName: 'Korean', nativeName: '한국어', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'hi', bcp47: 'hi', englishName: 'Hindi', nativeName: 'हिन्दी', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'id', bcp47: 'id', englishName: 'Indonesian', nativeName: 'Bahasa Indonesia', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'vi', bcp47: 'vi', englishName: 'Vietnamese', nativeName: 'Tiếng Việt', scriptDirection: 'ltr', isAnalysisLanguage: false },
  { code: 'it', bcp47: 'it', englishName: 'Italian', nativeName: 'Italiano', scriptDirection: 'ltr', isAnalysisLanguage: false },
] as const satisfies readonly LaunchLanguage[];

/**
 * The launch output-language union. Derived from the catalog objects above, so
 * the TYPE cannot drift from the DATA: there is one literal list, not two.
 */
export type LaunchLanguageCode = (typeof LAUNCH_LANGUAGES)[number]['code'];

/** `launch-languages.v1.json#/output_language_codes`, derived — never re-typed. */
export const LAUNCH_LANGUAGE_CODES: readonly LaunchLanguageCode[] = LAUNCH_LANGUAGES.map(
  (language) => language.code,
);

export const LAUNCH_LANGUAGE_COUNT = LAUNCH_LANGUAGE_CODES.length;

const LAUNCH_LANGUAGE_CODE_SET: ReadonlySet<string> = new Set<string>(LAUNCH_LANGUAGE_CODES);

export const LAUNCH_LANGUAGE_BY_CODE: ReadonlyMap<LaunchLanguageCode, LaunchLanguage> = new Map(
  LAUNCH_LANGUAGES.map((language): [LaunchLanguageCode, LaunchLanguage] => [language.code, language]),
);

const analysisLanguageEntry = LAUNCH_LANGUAGES.find((language) => language.isAnalysisLanguage);
if (!analysisLanguageEntry) {
  throw new Error('launchLanguages: catalog has no analysis language; the catalog is corrupt.');
}

/**
 * The bilingual analysis language. The analysis view renders exactly one line
 * per source turn — the selected English translation for non-English source, or
 * the source text itself for English source. It never renders source and
 * translation as two independent conversational turns.
 */
export const ANALYSIS_LANGUAGE_CODE: LaunchLanguageCode = analysisLanguageEntry.code;

/**
 * Deepgram recognition-mode codes. These configure SOURCE transcription. They
 * are not languages, are never translation outputs, and are never rendered as a
 * language choice to a person.
 */
export const DEEPGRAM_RECOGNITION_MODE_CODES = ['multi'] as const;
export type DeepgramRecognitionModeCode = (typeof DEEPGRAM_RECOGNITION_MODE_CODES)[number];

const DEEPGRAM_RECOGNITION_MODE_CODE_SET: ReadonlySet<string> = new Set<string>(
  DEEPGRAM_RECOGNITION_MODE_CODES,
);

/**
 * Codes a downstream surface might wrongly carry forward from a legacy list.
 * Mirrors `launch-languages.v1.json#/not_output_languages/codes`. Exported so a
 * surface replacing a legacy picker can assert it is not shipping one of these,
 * and so telemetry can name the exact rejected value.
 */
export const NOT_OUTPUT_LANGUAGE_CODES = [
  'multi',
  'nl',
  'ar',
  'th',
  'tl',
  'pl',
  'uk',
  'auto',
  '',
] as const;

/**
 * Exact membership test. `Set.has` is exact string equality — this is the
 * BI-13 guarantee in code. Do not replace it with `startsWith`, `includes`,
 * `localeCompare`, `Intl.Locale` normalisation, or any detection heuristic:
 * `en-US`, `EN`, `english` and `e` are all NOT launch language codes.
 */
export function isLaunchLanguageCode(value: unknown): value is LaunchLanguageCode {
  return typeof value === 'string' && LAUNCH_LANGUAGE_CODE_SET.has(value);
}

/** Exact membership test allowing the explicit `null` that means "not yet known". */
export function isOptionalLaunchLanguageCode(value: unknown): value is LaunchLanguageCode | null {
  return value === null || isLaunchLanguageCode(value);
}

/** True only for a Deepgram recognition mode such as `multi`. Never a translation output. */
export function isDeepgramRecognitionModeCode(value: unknown): value is DeepgramRecognitionModeCode {
  return typeof value === 'string' && DEEPGRAM_RECOGNITION_MODE_CODE_SET.has(value);
}

/**
 * Exact language equality, including the null case. Two unknown languages are
 * NOT equal to each other: null means "not yet known", so it can never satisfy
 * a "recipient already speaks this language" comparison.
 */
export function languageEquals(
  left: LaunchLanguageCode | null | undefined,
  right: LaunchLanguageCode | null | undefined,
): boolean {
  if (!isLaunchLanguageCode(left) || !isLaunchLanguageCode(right)) {
    return false;
  }
  return left === right;
}

export function getLaunchLanguage(code: unknown): LaunchLanguage | undefined {
  return isLaunchLanguageCode(code) ? LAUNCH_LANGUAGE_BY_CODE.get(code) : undefined;
}

/**
 * Narrow an untrusted value or throw. Use at a boundary (query string, stored
 * preference, participant attribute) where a bad code must not silently become
 * a default: choosing a default language for someone is a safety decision.
 */
export function assertLaunchLanguageCode(value: unknown, context: string): LaunchLanguageCode {
  if (!isLaunchLanguageCode(value)) {
    throw new Error(
      `${context}: ${JSON.stringify(value)} is not a launch output language. ` +
        `Legal codes: ${LAUNCH_LANGUAGE_CODES.join(', ')}.`,
    );
  }
  return value;
}
